import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Fade,
  Grid,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyIcon from '@mui/icons-material/Key';
import { solicitarValidacaoEmail } from '../../services/api';
import toast from 'react-hot-toast';

const ValidationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState<'email' | 'codigo'>('email');
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);

  // Pegar dados do state (vindo da SearchPage)
  const servidorData = location.state?.servidorData;

  useEffect(() => {
    if (!servidorData) {
      navigate('/consulta');
    }
  }, [servidorData, navigate]);

  // Função para formatar telefone
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0,2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0,2)}) ${numbers.slice(2,6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0,2)}) ${numbers.slice(2,7)}-${numbers.slice(7,11)}`;
  };

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setTelefone(formatted);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      toast.loading('Enviando código de validação...', { id: 'validation' });
      const response = await solicitarValidacaoEmail(
        servidorData.servidor.cpf,
        email,
        telefone || undefined // só envia se preenchido
      );

      setToken(response.token);
      setSuccess(true);
      setActiveTab('codigo');
      toast.success('Código enviado para seu email!' + (telefone ? ' e para seu telefone via SMS' : ''), { id: 'validation' });
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Erro ao enviar código de validação';
      setError(errorMsg);
      toast.error(errorMsg, { id: 'validation' });
    } finally {
      setLoading(false);
    }
  };

  const handleCodigoChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCodigo = [...codigo];
      newCodigo[index] = value;
      setCodigo(newCodigo);
      setError('');

      // Auto-focus next input
      if (value && index < 5) {
        setTimeout(() => {
          const nextInput = document.getElementById(`code-input-${index + 1}`);
          nextInput?.focus();
        }, 10);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      setTimeout(() => {
        const prevInput = document.getElementById(`code-input-${index - 1}`);
        prevInput?.focus();
      }, 10);
    }
  };

  const handleValidarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();

    const codigoCompleto = codigo.join('');
    if (codigoCompleto.length !== 6) {
      setError('Digite todos os 6 dígitos do código');
      return;
    }

    setError('');
    setLoading(true);

    try {
      toast.loading('Validando código...', { id: 'validate-code' });

      // Usar a função de validação do backend (igual antes)
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/auth/validar-codigo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, codigo: codigoCompleto })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao validar código');
      }

      // Salvar dados da sessão
      if (data.session_token) {
        localStorage.setItem('token', data.session_token);
      }
      if (data.servidor?.id) {
        localStorage.setItem('servidorData', JSON.stringify(data.servidor));
        navigate(`/documentos/${data.servidor.id}`);
      }

      toast.success('Código validado com sucesso!', { id: 'validate-code' });
    } catch (err: any) {
      const errorMsg = err.message || 'Erro ao validar código';
      setError(errorMsg);
      toast.error(errorMsg, { id: 'validate-code' });
    } finally {
      setLoading(false);
    }
  };

  const handleReenviarCodigo = async () => {
    if (!email || !servidorData) return;

    setLoading(true);

    try {
      toast.loading('Reenviando código...', { id: 'resend-code' });
      const response = await solicitarValidacaoEmail(servidorData.servidor.cpf, email, telefone || undefined);

      setToken(response.token);
      toast.success('Código reenviado com sucesso!', { id: 'resend-code' });

      // Limpar código anterior
      setCodigo(['', '', '', '', '', '']);

      // Focar no primeiro campo
      setTimeout(() => {
        const firstInput = document.getElementById('code-input-0');
        firstInput?.focus();
      }, 100);

    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao reenviar código', {
        id: 'resend-code',
        duration: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  if (!servidorData) {
    return null;
  }

  return (
    <Fade in={true} timeout={500}>
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          {activeTab === 'email' ? (
            <>
              <EmailIcon
                sx={{
                  fontSize: 60,
                  color: 'primary.main',
                  mb: 2
                }}
              />

              <Typography variant="h5" gutterBottom color="primary.main">
                Confirmação de Contato
              </Typography>

              <Typography variant="body1" paragraph color="text.secondary">
                Para prosseguir com a assinatura dos documentos, precisamos confirmar seu email e telefone.
                Informe seu telefone para receber o código também por SMS.
              </Typography>

              <Box sx={{ my: 3, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {servidorData.servidor.nome_completo}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CPF: {servidorData.servidor.cpf}
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="E-mail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="outlined"
                  margin="normal"
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                  helperText="Digite o email onde deseja receber o código"
                />

                <TextField
                  fullWidth
                  label="Telefone"
                  value={telefone}
                  onChange={handleTelefoneChange}
                  variant="outlined"
                  margin="normal"
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                  placeholder="(99) 99999-9999"
                  helperText="Se informado, você receberá o código também por SMS"
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !email}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Enviando...
                    </>
                  ) : (
                    'Enviar Código de Validação'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <KeyIcon
                sx={{
                  fontSize: 60,
                  color: 'primary.main',
                  mb: 2
                }}
              />

              <Typography variant="h5" gutterBottom color="primary.main">
                Inserir Código de Validação
              </Typography>

              <Box
                sx={{
                  mb: 3,
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: '#e8f4fd',
                  border: '1px solid #90caf9',
                }}
              >
                <Typography variant="body2" color="#1565c0" gutterBottom>
                  Código enviado para: <strong>{email}</strong>
                </Typography>
                {telefone && (
                  <Typography variant="body2" color="#1565c0" gutterBottom>
                    e para o telefone: <strong>{telefone}</strong>
                  </Typography>
                )}
                <Typography variant="body2" color="#1565c0">
                  Olá, <strong>{servidorData.servidor.nome_completo}</strong>
                </Typography>
              </Box>

              <Typography variant="body1" paragraph color="text.secondary">
                Digite o código de 6 dígitos que você recebeu.
              </Typography>

              <form onSubmit={handleValidarCodigo}>
                <Grid container spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                  {codigo.map((digit, index) => (
                    <Grid item key={index}>
                      <TextField
                        id={`code-input-${index}`}
                        value={digit}
                        onChange={(e) => handleCodigoChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        inputProps={{
                          maxLength: 1,
                          style: {
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            padding: '10px 0'
                          }
                        }}
                        sx={{
                          width: 55,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& input': {
                              textAlign: 'center',
                              padding: '10px 0'
                            }
                          }
                        }}
                        disabled={loading}
                        autoFocus={index === 0}
                      />
                    </Grid>
                  ))}
                </Grid>

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Vencimento:
                      </Typography>
                      <Typography variant="body2">
                        15 minutos
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Verifique:
                      </Typography>
                      <Typography variant="body2">
                        Caixa de spam
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || codigo.join('').length !== 6}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold',
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Validando...
                    </>
                  ) : (
                    'Validar Código e Acessar Documentos'
                  )}
                </Button>
              </form>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  onClick={handleReenviarCodigo}
                  variant="outlined"
                  size="small"
                  disabled={loading}
                >
                  Reenviar Código
                </Button>

                <Button
                  onClick={() => setActiveTab('email')}
                  variant="text"
                  size="small"
                  disabled={loading}
                >
                  Voltar
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};

export default ValidationPage;